import React, { useState, useEffect } from 'react';
import { uploadData, getUrl, remove, list } from 'aws-amplify/storage';
import './FileExplorer.css';

const folderStructure = [
  '01 Ideas',
  '02 Ready For Band (agreed)',
  '03 Rehearsal Recordings',
  '04 Shed Studio Demos',
  '05 Posh Studio Recordings',
  '06 KEXP Sessions'
];

const FileExplorer = () => {
  const [currentFolder, setCurrentFolder] = useState('');
  const [files, setFiles] = useState([]);
  const [folders, setFolders] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [showNewFolderInput, setShowNewFolderInput] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [error, setError] = useState('');
  const [fileToMove, setFileToMove] = useState(null);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [isMoving, setIsMoving] = useState(false);
  const [allFolders, setAllFolders] = useState([]);

  useEffect(() => {
    listFilesAndFolders();
  }, [currentFolder]);

  const listFilesAndFolders = async () => {
    try {
      const fileList = await list({
        prefix: currentFolder ? `${currentFolder}/` : ''
      });
      
      // Process the results to separate files and folders
      const processedItems = processS3Items(fileList.items, currentFolder);
      setFiles(processedItems.files);
      setFolders(processedItems.folders);
    } catch (error) {
      console.log('Error listing files and folders: ', error);
      setError('Failed to load files and folders. Please try again.');
    }
  };

  // Process S3 items to separate files and folders
  const processS3Items = (items, currentPath) => {
    const files = [];
    const folders = new Set();
    const currentPrefix = currentPath ? `${currentPath}/` : '';
    
    items.forEach(item => {
      // Skip the current folder itself
      if (item.key === currentPrefix) return;
      
      // Remove the current path prefix to get the relative path
      const relativePath = item.key.slice(currentPrefix.length);
      
      // Check if this is a folder (contains '/' in the relative path)
      if (relativePath.includes('/')) {
        // Extract the folder name (first segment of the relative path)
        const folderName = relativePath.split('/')[0];
        folders.add(folderName);
      } else {
        // This is a file in the current directory
        files.push({
          ...item,
          name: relativePath
        });
      }
    });
    
    return {
      files,
      folders: Array.from(folders)
    };
  };

  // Fetch all folders in the bucket including subdirectories
  const fetchAllFolders = async () => {
    try {
      const fileList = await list();
      
      // Extract all folder paths
      const folders = new Set();
      folders.add(''); // Add root folder
      
      fileList.items.forEach(item => {
        const key = item.key;
        const parts = key.split('/');
        
        // Skip the last part if it's a file
        const depth = key.endsWith('/') ? parts.length : parts.length - 1;
        
        // Add each folder level
        let path = '';
        for (let i = 0; i < depth; i++) {
          if (parts[i]) {
            path = path ? `${path}/${parts[i]}` : parts[i];
            folders.add(path);
          }
        }
      });
      
      // Convert to array and sort
      return Array.from(folders).sort();
    } catch (error) {
      console.error('Error fetching all folders:', error);
      return [];
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      // Don't set upload status for file selection
      // setUploadStatus(`Selected: ${file.name} (${formatFileSize(file.size)})`);
    }
  };

  const startUpload = async () => {
    if (!selectedFile) {
      setUploadStatus('No file selected');
      return;
    }
    
    // Calculate file size in MB
    const fileSizeMB = selectedFile.size / (1024 * 1024);
    console.log(`File size: ${fileSizeMB.toFixed(2)} MB`);
    setUploadStatus(`Preparing to upload ${fileSizeMB.toFixed(2)} MB file...`);
    
    try {
      setUploading(true);
      setUploadProgress(0);
      
      // Determine the key based on current folder
      const key = currentFolder 
        ? `${currentFolder}/${selectedFile.name}`
        : selectedFile.name;
      
      // Create a progress-tracking wrapper around the file
      const fileSize = selectedFile.size;
      let uploadedSize = 0;
      let progressInterval;
      
      // Set up a timer to simulate progress updates
      progressInterval = setInterval(() => {
        // Increment by a small percentage (adjust as needed)
        uploadedSize = Math.min(uploadedSize + fileSize * 0.02, fileSize * 0.95);
        const percentComplete = Math.round((uploadedSize / fileSize) * 100);
        const loadedMB = (uploadedSize / (1024 * 1024)).toFixed(2);
        const totalMB = (fileSize / (1024 * 1024)).toFixed(2);
        
        setUploadProgress(percentComplete);
        setUploadStatus(`Uploading: ${percentComplete}% (${loadedMB} MB of ${totalMB} MB)`);
      }, 500); // Update every 500ms
      
      // Perform the actual upload
      await uploadData({
        key: key,
        data: selectedFile,
        options: {
          contentType: selectedFile.type || 'application/octet-stream'
        }
      }).result;
      
      // Clear the interval and set to 100%
      clearInterval(progressInterval);
      setUploadProgress(100);
      setUploadStatus(`Upload complete! (${(fileSize / (1024 * 1024)).toFixed(2)} MB)`);
      
      console.log('Upload completed successfully');
      setUploading(false);
      setSelectedFile(null);
      // Reset the file input
      document.getElementById('file-upload').value = '';
      setTimeout(() => setUploadStatus(''), 3000);
      listFilesAndFolders();
    } catch (error) {
      console.error('Error uploading file:', error);
      setUploading(false);
      setUploadStatus(`Upload failed: ${error.message}`);
    }
  };

  const handleDownload = async (key) => {
    try {
      const fileName = key.split('/').pop();
      
      // Amplify v6 syntax with proper download options
      const result = await getUrl({
        key: key,
        options: {
          download: true,
          validateObjectExistence: false,  // Skip existence check for performance
          expiresIn: 300  // URL expires in 5 minutes
        }
      });
      
      // Create a temporary anchor element
      const a = document.createElement('a');
      a.href = result.url.toString();  // Amplify v6 returns a URL object
      a.download = fileName;
      a.style.display = 'none';
      
      // Add to DOM, click, and remove
      document.body.appendChild(a);
      a.click();
      
      // Clean up
      setTimeout(() => {
        document.body.removeChild(a);
      }, 100);
      
    } catch (error) {
      console.log('Error downloading file: ', error);
      setError('Failed to download file. Please try again.');
    }
  };
  const handleDelete = async (key) => {
    // Show confirmation dialog before deleting
    if (!window.confirm('Are you sure you want to delete this file? This action cannot be undone.')) {
      return; // User canceled the deletion
    }
    
    try {
      await remove({ key });
      listFilesAndFolders();
    } catch (error) {
      console.log('Error deleting file: ', error);
      setError('Failed to delete file. Please try again.');
    }
  };

  const handleMoveFile = async (destinationFolder) => {
    if (!fileToMove) return;
    
    try {
      setIsMoving(true);
      
      // Get the file name from the key
      const fileName = fileToMove.name;
      
      // Construct the source and destination keys
      const sourceKey = fileToMove.key;
      const destinationKey = destinationFolder 
        ? `${destinationFolder}/${fileName}` 
        : fileName;
      
      // Skip if source and destination are the same
      if (sourceKey === destinationKey) {
        setShowMoveModal(false);
        setIsMoving(false);
        return;
      }
      
      // Get the file content
      const result = await getUrl({
        key: sourceKey
      });
      
      // Fetch the file content
      const response = await fetch(result.url);
      const blob = await response.blob();
      
      // Upload to the new location
      await uploadData({
        key: destinationKey,
        data: blob,
        options: {
          contentType: blob.type
        }
      }).result;
      
      // Delete the original file
      await remove({ key: sourceKey });
      
      // Refresh the file list
      listFilesAndFolders();
      
      // Close the modal
      setShowMoveModal(false);
      setIsMoving(false);
    } catch (error) {
      console.error('Error moving file:', error);
      setError('Failed to move file. Please try again.');
      setIsMoving(false);
    }
  };

  const createFolder = async () => {
    if (!newFolderName.trim()) {
      setError('Please enter a folder name.');
      return;
    }
    
    try {
      // Create an empty file with a trailing slash to represent a folder
      const folderKey = currentFolder 
        ? `${currentFolder}/${newFolderName.trim()}/` 
        : `${newFolderName.trim()}/`;
      
      await uploadData({
        key: folderKey,
        data: '',
        options: {
          contentType: 'application/x-directory'
        }
      }).result;
      
      // Reset the input and refresh the file list
      setNewFolderName('');
      setShowNewFolderInput(false);
      listFilesAndFolders();
    } catch (error) {
      console.error('Error creating folder:', error);
      setError('Failed to create folder. Please try again.');
    }
  };

  const navigateToFolder = (folder) => {
    setCurrentFolder(currentFolder ? `${currentFolder}/${folder}` : folder);
  };

  const navigateUp = () => {
    if (!currentFolder) return;
    
    const parts = currentFolder.split('/');
    if (parts.length === 1) {
      setCurrentFolder('');
    } else {
      parts.pop();
      setCurrentFolder(parts.join('/'));
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="container">
      <h2>Files</h2>
      
      {error && (
        <div className="error-message">
          {error}
          <button className="dismiss-button" onClick={() => setError('')}>×</button>
        </div>
      )}
      
      {!currentFolder ? (
        <div>
          <div className="folder-actions">
            <button 
              className="new-folder-button"
              onClick={() => setShowNewFolderInput(!showNewFolderInput)}
            >
              {showNewFolderInput ? 'Cancel' : 'New Folder'}
            </button>
          </div>
          
          {showNewFolderInput && (
            <div className="new-folder-input">
              <input
                type="text"
                placeholder="Enter folder name"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
              />
              <button onClick={createFolder}>Create</button>
            </div>
          )}
          
          <div className="folder-grid">
            {folderStructure.map((folder) => (
              <div 
                key={folder} 
                className="folder" 
                onClick={() => setCurrentFolder(folder)}
              >
                <h3 className="folder-title">{folder}</h3>
                <p>Click to open</p>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div>
          <div className="folder-header">
            <button onClick={navigateUp}>Back</button>
            <h2>{currentFolder}</h2>
            <button 
              className="new-folder-button"
              onClick={() => setShowNewFolderInput(!showNewFolderInput)}
            >
              {showNewFolderInput ? 'Cancel' : 'New Folder'}
            </button>
          </div>
          
          {showNewFolderInput && (
            <div className="new-folder-input">
              <input
                type="text"
                placeholder="Enter folder name"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
              />
              <button onClick={createFolder}>Create</button>
            </div>
          )}
          
          <div className="upload-area">
            <h3>Upload Files</h3>
            <p className="upload-info">Select a file and click the Upload button</p>
            <div className="upload-controls">
              <input 
                id="file-upload"
                type="file" 
                onChange={handleFileSelect} 
                disabled={uploading}
              />
              <button 
                className="upload-button" 
                onClick={startUpload} 
                disabled={!selectedFile || uploading}
              >
                Upload
              </button>
            </div>
            {selectedFile && (
              <div className="selected-file">
                <p>Selected: {selectedFile.name} ({formatFileSize(selectedFile.size)})</p>
              </div>
            )}
            {uploading && (
              <div className="upload-progress">
                <div className="progress-bar">
                  <div 
                    className="progress-bar-fill" 
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <p>{uploadStatus || `${uploadProgress}% Uploaded`}</p>
              </div>
            )}
            {!uploading && uploadStatus && !uploadStatus.startsWith('Selected:') && (
              <p className="upload-status">{uploadStatus}</p>
            )}
          </div>
          
          {folders.length > 0 && (
            <div className="folders-list">
              <h3>Folders</h3>
              <div className="folder-grid">
                {folders.map((folder) => (
                  <div 
                    key={folder} 
                    className="folder" 
                    onClick={() => navigateToFolder(folder)}
                  >
                    <h3 className="folder-title">{folder}</h3>
                    <p>Click to open</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          <div className="file-list">
            <h3>Files</h3>
            {files.length === 0 ? (
              <p>No files in this folder yet.</p>
            ) : (
              <ul>
                {files.map((file) => (
                  <li key={file.key} className="file-item">
                    <span className="file-name">{file.name}</span>
                    <span className="file-size">{file.size ? formatFileSize(file.size) : 'Unknown size'}</span>
                    <div className="file-actions">
                      <button 
                        className="move-button" 
                        onClick={async (e) => {
                          e.stopPropagation();
                          setFileToMove(file);
                          const folders = await fetchAllFolders();
                          setAllFolders(folders);
                          setShowMoveModal(true);
                        }}
                      >
                        Move
                      </button>
                      <button onClick={() => handleDownload(file.key)}>Download</button>
                      <button onClick={() => handleDelete(file.key)}>Delete</button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
      
      {showMoveModal && (
        <div className="move-modal">
          <div className="move-modal-content">
            <h3>Move File</h3>
            <p>Select destination folder for: {fileToMove?.name}</p>
            
            <div className="folder-list">
              {allFolders.map((folder) => {
                // Calculate indentation level
                const level = folder ? folder.split('/').length : 0;
                const indent = level * 16; // 16px per level
                
                return (
                  <div 
                    key={folder}
                    className="folder-item" 
                    onClick={() => !isMoving && handleMoveFile(folder)}
                    style={{ 
                      opacity: isMoving ? 0.5 : 1, 
                      cursor: isMoving ? 'not-allowed' : 'pointer',
                      paddingLeft: `${indent}px`
                    }}
                  >
                    {folder === '' ? 'Root' : folder.split('/').pop()}
                  </div>
                );
              })}
            </div>
            
            <div className="modal-actions">
              <button 
                onClick={() => !isMoving && setShowMoveModal(false)} 
                disabled={isMoving}
              >
                {isMoving ? 'Moving...' : 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div className="band-logo">
        <img src="/lm.png" alt="Band Logo" style={{ maxWidth: '300px', marginTop: '40px' }} />
      </div>
    </div>
  );
};

export default FileExplorer;
